import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CameraAlt from '@mui/icons-material/esm/CameraAlt';
import Keyboard from '@mui/icons-material/esm/Keyboard';
import QrCodeScanner from '@mui/icons-material/esm/QrCodeScanner';
import type { IScannerControls } from '@zxing/browser';

interface BarcodeScannerProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  onScan: (code: string) => void;
}

export default function BarcodeScanner({
  open,
  title = 'Scan Barcode',
  onClose,
  onScan,
}: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const scannedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    if (!open) {
      scannedRef.current = false;
      return undefined;
    }

    let cancelled = false;
    scannedRef.current = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera scanning is unavailable on this device. Enter the code manually.');
        return;
      }

      try {
        const [{ BrowserMultiFormatReader }, { NotFoundException }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current ?? undefined,
          (result, scanError) => {
            if (result && !scannedRef.current) {
              scannedRef.current = true;
              const code = result.getText();
              controlsRef.current?.stop();
              onScan(code);
              return;
            }

            if (scanError && !(scanError instanceof NotFoundException)) {
              setError('Unable to read the barcode. Keep the code inside the camera frame or enter it manually.');
            }
          }
        );

        if (cancelled) {
          controls.stop();
        } else {
          controlsRef.current = controls;
          setError(null);
        }
      } catch (scanError) {
        if (!cancelled) {
          setError(scanError instanceof Error ? scanError.message : 'Camera permission was denied. Enter the code manually.');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [onScan, open]);

  const submitManualCode = () => {
    const code = manualCode.trim();
    if (!code) return;
    scannedRef.current = true;
    onScan(code);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {error && <Alert severity="warning">{error}</Alert>}
          <Box
            sx={{
              bgcolor: 'grey.900',
              borderRadius: 1,
              minHeight: 260,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              style={{ width: '100%', minHeight: 260, objectFit: 'cover', display: 'block' }}
            />
            <Stack
              spacing={0.5}
              alignItems="center"
              sx={{
                color: 'common.white',
                left: 0,
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 16,
              }}
            >
              <QrCodeScanner />
              <Typography variant="body2">Align the code in view</Typography>
            </Stack>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              fullWidth
              label="Manual code"
              value={manualCode}
              onChange={(event) => setManualCode(event.target.value)}
              InputProps={{ startAdornment: <Keyboard fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <Button variant="outlined" onClick={submitManualCode} disabled={!manualCode.trim()} startIcon={<CameraAlt />}>
              Use Code
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
